import {
  ElectronAPI,
  getElectronAPI,
  NodeInfo,
  NodeInfoType,
  rootNodeInfo,
  runsInsideElectron,
  TransformationMatrix,
} from "@org-avp/avp-avengers-ui-framework-electron-api";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MsgModalModel } from "../../../hooks/use-message-modal-helper";

export const DEFAULT_4X3_MATRIX = [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0];

export interface Matrix4x3 {
  xx: number;
  xy: number;
  xz: number;
  yx: number;
  yy: number;
  yz: number;
  zx: number;
  zy: number;
  zz: number;
  tx: number;
  ty: number;
  tz: number;

  setXX(value: number): void;

  setXY(value: number): void;

  setXZ(value: number): void;

  setYX(value: number): void;

  setYY(value: number): void;

  setYZ(value: number): void;

  setZX(value: number): void;

  setZY(value: number): void;

  setZZ(value: number): void;

  setTX(value: number): void;

  setTY(value: number): void;

  setTZ(value: number): void;
}

export interface PositionDisplayData {
  data: number[];
}

export interface PositionDataHelper {
  // content to display
  matrix4x3?: Matrix4x3;

  // enable flag for operations
  canAddDefaultPositionData: boolean;
  canGetPositionData: boolean;
  canMirrorPositionData: boolean;
  canDeletePositionData: boolean;

  // whether position data is currently being fetched from as
  isGettingPositionData: boolean;

  // the actual operations
  addDefaultPositionData(): void;

  getPositionData(absolute: boolean): void;

  mirrorPositionData(): void;

  deletePositionData(): void;
}

export const usePositionDataHelper: (
  positionData: PositionDisplayData | undefined,
  setPositionData: (positionData?: PositionDisplayData) => void,
  marketingRuleSelected: boolean,
  setMsgModalModel?: (msgModalModel: MsgModalModel) => boolean,
) => PositionDataHelper = (positionData, setPositionData, marketingRuleSelected, setMsgModalModel) => {
  const { t } = useTranslation("asConnector");

  // --- some shortcut for functionality needed later ---
  const pd = useMemo(() => positionData, [positionData]);
  const setPD = useCallback(
    (positionData?: PositionDisplayData) => setPositionData(positionData),
    [setPositionData],
  );

  const valid = useMemo(
    () => pd !== null && pd !== undefined && pd.data.length === 12,
    [pd],
  );

  const setModalUserFeedback = useCallback((msgModalModel: MsgModalModel) => {
    if (setMsgModalModel) {
      setMsgModalModel(msgModalModel);
    }
  }, [setMsgModalModel]);

  // --- getters ---
  const getFromPD = useCallback((index: number) => valid ? pd?.data[index] : undefined, [pd?.data, valid]);

  const xx = useMemo(() => getFromPD(0), [getFromPD]);
  const xy = useMemo(() => getFromPD(1), [getFromPD]);
  const xz = useMemo(() => getFromPD(2), [getFromPD]);
  const yx = useMemo(() => getFromPD(3), [getFromPD]);
  const yy = useMemo(() => getFromPD(4), [getFromPD]);
  const yz = useMemo(() => getFromPD(5), [getFromPD]);
  const zx = useMemo(() => getFromPD(6), [getFromPD]);
  const zy = useMemo(() => getFromPD(7), [getFromPD]);
  const zz = useMemo(() => getFromPD(8), [getFromPD]);
  const tx = useMemo(() => getFromPD(9), [getFromPD]);
  const ty = useMemo(() => getFromPD(10), [getFromPD]);
  const tz = useMemo(() => getFromPD(11), [getFromPD]);

  // --- setters ---
  const setInPD = useCallback(
    (value: number, index: number) => valid && pd !== null && pd !== undefined && setPD({ data: setInArray(pd.data, index, value) }),
    [pd, setPD, valid],
  );

  const setXX = useCallback((value: number) => setInPD(value, 0), [setInPD]);
  const setXY = useCallback((value: number) => setInPD(value, 1), [setInPD]);
  const setXZ = useCallback((value: number) => setInPD(value, 2), [setInPD]);
  const setYX = useCallback((value: number) => setInPD(value, 3), [setInPD]);
  const setYY = useCallback((value: number) => setInPD(value, 4), [setInPD]);
  const setYZ = useCallback((value: number) => setInPD(value, 5), [setInPD]);
  const setZX = useCallback((value: number) => setInPD(value, 6), [setInPD]);
  const setZY = useCallback((value: number) => setInPD(value, 7), [setInPD]);
  const setZZ = useCallback((value: number) => setInPD(value, 8), [setInPD]);
  const setTX = useCallback((value: number) => setInPD(value, 9), [setInPD]);
  const setTY = useCallback((value: number) => setInPD(value, 10), [setInPD]);
  const setTZ = useCallback((value: number) => setInPD(value, 11), [setInPD]);

  // === return values ===
  // --- data matrix ---
  const matrix4x3 = useMemo(
    () =>
      valid
      && xx !== undefined && xy !== undefined && xz !== undefined && yx !== undefined && yy !== undefined && yz !== undefined
      && zx !== undefined && zy !== undefined && zz !== undefined && tx !== undefined && ty !== undefined && tz !== undefined
        ? { xx, xy, xz, yx, yy, yz, zx, zy, zz, tx, ty, tz, setXX, setXY, setXZ, setYX, setYY, setYZ, setZX, setZY, setZZ, setTX, setTY, setTZ }
        : undefined,
    [setTX, setTY, setTZ, setXX, setXY, setXZ, setYX, setYY, setYZ, setZX, setZY, setZZ, tx, ty, tz, valid, xx, xy, xz, yx, yy, yz, zx, zy, zz],
  );

  // --- status flags for buttons ---
  const canAddDefaultPositionData = useMemo(() => marketingRuleSelected && (pd === undefined || pd === null), [marketingRuleSelected, pd]);

  const electronAPI: ElectronAPI | undefined = useMemo(() => runsInsideElectron() ? getElectronAPI() : undefined, []);
  const canGetPositionData = useMemo(() => {
    return marketingRuleSelected && pd != null && electronAPI != null;
  }, [electronAPI, marketingRuleSelected, pd]);
  const canMirrorPositionData = useMemo(() => valid, [valid]);
  const canDeletePositionData = useMemo(() => pd !== null && pd !== undefined, [pd]);

  // --- button functions ---
  const addDefaultPositionData = useCallback(() => {
    if (canAddDefaultPositionData) {
      setPD({ data: DEFAULT_4X3_MATRIX });
    }
  }, [canAddDefaultPositionData, setPD]);

  const [isGettingPositionData, setIsGettingPositionData] = useState(false);
  const getPositionData = useCallback((absolute: boolean) => {
    if (canGetPositionData && electronAPI) {
      setIsGettingPositionData(true);
      electronAPI.invoke.asConnectorNodeGetSelection().then((nodeInfos) => {
        if (nodeInfos.length === 1) {
          const nodeInfo = nodeInfos[0];

          if (!nodeInfo.AsId) {
            throw new Error("Selected node in authoring system has no AsId!");
          }

          getTransformation(electronAPI, nodeInfo, absolute)
            .then((transformationMatrix) => {
              setPD({
                data: [
                  transformationMatrix.Xx,
                  transformationMatrix.Xy,
                  transformationMatrix.Xz,
                  transformationMatrix.Yx,
                  transformationMatrix.Yy,
                  transformationMatrix.Yz,
                  transformationMatrix.Zx,
                  transformationMatrix.Zy,
                  transformationMatrix.Zz,
                  transformationMatrix.Tx,
                  transformationMatrix.Ty,
                  transformationMatrix.Tz,
                ],
              });
            })
            .catch((e) => {
              console.error("Failed to get transformation for selected node from authoring system!", nodeInfo, e);
              const title = t("getSelectedNodeTransformation.error.title");
              const message = t("getSelectedNodeTransformation.error.message");
              setModalUserFeedback({ type: "ERROR", title, message });
            });
        } else {
          console.info(`Found ${nodeInfos.length} selected nodes: Skipped "getTransformation".`);
          const title = t("getSelectedNodeTransformation.expectedSingleSelectedNode.title");
          const message = t("getSelectedNodeTransformation.expectedSingleSelectedNode.message");
          setModalUserFeedback({ type: "WARNING", title, message });
        }
      }).catch((e) => {
        console.error("Failed to get selected nodes from authoring system!", e);
        const title = t("getSelectedNodeTransformation.error.title");
        const message = t("getSelectedNodeTransformation.error.message");
        setModalUserFeedback({ type: "ERROR", title, message });
      }).finally(() => {
        setIsGettingPositionData(false);
      });
    }
  }, [canGetPositionData, electronAPI, setModalUserFeedback, setPD, t]);
  const mirrorPositionData = useCallback(() => {
    if (canMirrorPositionData && pd !== null && pd !== undefined) {
      const old = pd.data;
      setPD({
        ...pd,
        data: [old[0], invert(old[1]), old[2], old[3], invert(old[4]), old[5], old[6], invert(old[7]), old[8], old[9], invert(old[10]), old[11]],
      });
    }
  }, [canMirrorPositionData, pd, setPD]);
  const deletePositionData = useCallback(() => {
    if (canDeletePositionData) {
      setPD(undefined);
    }
  }, [canDeletePositionData, setPD]);

  return {
    matrix4x3,
    canAddDefaultPositionData,
    canGetPositionData,
    canMirrorPositionData,
    canDeletePositionData,
    isGettingPositionData,
    addDefaultPositionData,
    getPositionData,
    mirrorPositionData,
    deletePositionData,
  };
};

async function getTransformation(electronAPI: ElectronAPI, nodeInfo: NodeInfo, absolute: boolean): Promise<TransformationMatrix> {
  return absolute
    ? getAbsoluteTransformation(electronAPI, nodeInfo)
    : getRelativeTransformation(electronAPI, nodeInfo);
}

async function getRelativeTransformation(electronAPI: ElectronAPI, nodeInfo: NodeInfo): Promise<TransformationMatrix> {
  return electronAPI.invoke.asConnectorNodeGetTransformation(nodeInfo);
}

async function getAbsoluteTransformation(electronAPI: ElectronAPI, nodeInfo: NodeInfo): Promise<TransformationMatrix> {
  return getParentNodeIds(electronAPI, nodeInfo)
    .then((parentNodeIds) => electronAPI.invoke.asConnectorNodeGetAbsoluteTransformation(nodeInfo, parentNodeIds))
    .catch((e) => {
      console.error("Failed to get absolute transformation from authoring system!", e);
      return Promise.reject(e);
    });
}

async function getParentNodeIds(electronAPI: ElectronAPI, nodeInfo: NodeInfo): Promise<string[]> {
  // Pass an empty array to asConnectorSceneGetStructure instead of "Object.values(NodeInfoType)".
  // Types are used as filter: an empty array is interpreted as "no filtering".
  const nodeInfoTypes: NodeInfoType[] = [];

  return electronAPI.invoke
    .asConnectorSceneGetStructure(rootNodeInfo, nodeInfoTypes)
    .then((nodeInfos) => {
      console.log(`Received ${nodeInfos.length} nodeInfos from AsConnector.`);

      // create nodeInfoLookup
      const nodeInfoLookup = new Map<string, NodeInfo>();
      nodeInfos.forEach((nodeInfo) => {
        nodeInfoLookup.set(nodeInfo.AsId, nodeInfo);
      });

      if (nodeInfoLookup.size < nodeInfos.length) {
        throw new Error("Expected unique AsIds.");
      }

      // CURRENTLY the asConnector
      // - checks "parentNodeIds" against scene graph paths from ROOT to node (the scene graph path includes the node)
      // - checks if all IDs of parentNodeIds are included in the checked scene graph path (the order is NOT checked)
      // => it is an option to pass parentNodeIds with "nodeInfo.AsId" included to perform a stricter test, BUT:
      // - a future AsConnector implementation may walk down the parentNodeIds path and search the node as child -> will fail if "nodeInfo.AsId" is included
      // - passing parentNodeIds with "nodeInfo.AsId" included would make the nodeInfo parameter redundant (if parentNodeIds's order is considered)

      // collect parentNodeIds: bottom-up
      const bottomUpParentNodeIds: string[] = []; // start with "[nodeInfo.AsId]" for a stricter test
      let currentNode: NodeInfo | undefined = nodeInfo;
      while (currentNode) {
        const parentId = currentNode.ParentNodeId;
        currentNode = nodeInfoLookup.get(parentId);
        if (currentNode) {
          bottomUpParentNodeIds.push(parentId);
        }
      }

      // return parentNodeIds: top-down (a future AsConnector implementation may check the order)
      return bottomUpParentNodeIds.reverse();
    });
}

function setInArray(array: number[], index: number, value: number): number[] {
  const result: number[] = [...array];
  result[index] = value;
  return result;
}

function invert(value: number): number {
  // prevent setting the inverted value to -0
  return value !== 0 ? value * -1 : value;
}
